import React, {useState} from 'react';
import axios from 'axios';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {Stack, Toolbar, Typography} from '@mui/material';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import {useAlert} from '../../alert/AlertContext';
import WorldSelectCombo, {isWorldAllowed} from './WorldSelectCombo';
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

function PaperComponent(props) {
    return (
        <Draggable
            handle="#player-location-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

const roundLocation = (location) => {
    return {
        x: parseFloat(location.x).toFixed(2),
        y: parseFloat(location.y).toFixed(2),
        z: parseFloat(location.z).toFixed(2),
        world: location.world,
        rotationY: parseFloat(location.rotationY).toFixed(2),
        rotationX: parseFloat(location.rotationX).toFixed(2),
    };
};

const PlayerLocationDialog = ({onSelect, onClose, defaultLocation, xzOnly, errorMessage = null, allowedWorlds = []}) => {

    const alert = useAlert();

    const [location, setLocation] = useState(defaultLocation ? roundLocation(defaultLocation) : {
        x: 0,
        y: 0,
        z: 0,
        world: allowedWorlds.length ? allowedWorlds[0] : 'ecexplore',
        rotationY: 0,
        rotationX: 0,
    });

    const handleChange = (e) => {
        const {name, value} = e.target;
        setLocation(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const onGetLocation = () => {
        axios.get(`/playerlocation`)
            .then(response => {
                const roundedLocation = {
                    x: parseFloat(response.data.x).toFixed(2),
                    y: parseFloat(response.data.y).toFixed(2),
                    z: parseFloat(response.data.z).toFixed(2),
                    world: response.data.world,
                    rotationY: parseFloat(response.data.rotationY).toFixed(2),
                    rotationX: parseFloat(response.data.rotationX).toFixed(2),
                };
                if (!isWorldAllowed(roundedLocation.world, allowedWorlds)) {
                    alert("Error Getting Location", `World ${roundedLocation.world} is not allowed here`);
                    return;
                }
                setLocation(roundedLocation);
                onSelect(roundedLocation);
            })
            .catch(error => {
                alert("Error Getting Location", error.response.data);
            });
    };

    const onSetLocation = () => {
        axios.post(`/playerlocation`, location);
    };

    return (
        <Dialog
            open={prompt !== null}
            onClose={onClose}
            fullWidth
            PaperComponent={PaperComponent}
        >
            <Toolbar variant='dense' disableGutters sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'space-between'
            }}>
                <DialogTitle id="player-location-dialog-title" sx={{cursor: 'move'}}>Get Location In Game</DialogTitle>
                <IconButton onClick={onClose}>
                    <CloseIcon/>
                </IconButton>
            </Toolbar>
            <DialogContent sx={{
                p: 0,
                ml: 2,
                mr: 2,
            }}>
                {errorMessage && <Typography sx={{marginBottom: '10px'}} color='red'>{errorMessage}, enter location
                    manually</Typography>}
                <Stack spacing={2} direction="row" sx={{marginBottom: '10px'}}>
                    <TextField
                        id="x"
                        name="x"
                        label="X"
                        variant="standard"
                        type="number"
                        value={location.x}
                        onChange={handleChange}
                        size='small'
                    />
                    {!xzOnly && <TextField
                        id="y"
                        name="y"
                        label="Y"
                        variant="standard"
                        type="number"
                        value={location.y}
                        onChange={handleChange}
                        size='small'
                    />
                    }
                    <TextField
                        id="z"
                        name="z"
                        label="Z"
                        variant="standard"
                        type="number"
                        value={location.z}
                        onChange={handleChange}
                        size='small'
                    />
                </Stack>
                {!xzOnly && <>
                    <WorldSelectCombo
                        name="world"
                        value={location.world}
                        variant="standard"
                        onChange={handleChange}
                        size='small'
                        allowedWorlds={allowedWorlds}
                    />
                    <Stack spacing={2} direction="row">
                        <TextField
                            id="rotationY"
                            name="rotationY"
                            label="Rotation Y"
                            variant="standard"
                            type="number"
                            value={location.rotationY}
                            onChange={handleChange}
                            size='small'
                        />
                        <TextField
                            id="rotationX"
                            name="rotationX"
                            label="Rotation X"
                            variant="standard"
                            type="number"
                            value={location.rotationX}
                            onChange={handleChange}
                            size='small'
                        />
                    </Stack>
                </>}
            </DialogContent>
            <DialogActions sx={{justifyContent: "space-between"}}>
                <Stack direction="row" spacing={1}>
                    <Button onClick={onGetLocation}>Get Location</Button>
                    <Button onClick={onSetLocation}>Goto</Button>
                </Stack>

                <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={() => onSelect(location)}>Confirm</Button>
                    <Button onClick={onClose}>Cancel</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default PlayerLocationDialog;
